export interface AidaClientConfig {
  name: string | undefined
  key: string | undefined
}

type AidaClientConfigMap = Record<string, Partial<AidaClientConfig> | undefined>

const AIDA_CLIENTS_ENV_KEY = 'VITE_AIDA_LIBRARY_CLIENTS'

function isCompleteClientConfig(config: Partial<AidaClientConfig> | undefined): boolean {
  return (
    typeof config?.name === 'string' &&
    config.name.trim() !== '' &&
    typeof config?.key === 'string' &&
    config.key.trim() !== ''
  )
}

function legacyAidaClientConfigFromEnv(env: ImportMetaEnv): AidaClientConfig {
  return {
    name: env.VITE_AIDA_LIBRARY_CLIENT_NAME,
    key: env.VITE_AIDA_LIBRARY_CLIENT_KEY,
  }
}

function parseAidaClientConfigMap(rawConfig: string | undefined): AidaClientConfigMap | null {
  if (!rawConfig) {
    return null
  }

  try {
    const parsed = JSON.parse(rawConfig)

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      console.warn(`${AIDA_CLIENTS_ENV_KEY} must be a JSON object.`)
      return null
    }

    return parsed as AidaClientConfigMap
  } catch (error) {
    console.warn(
      `Failed to parse ${AIDA_CLIENTS_ENV_KEY}. Falling back to legacy AIDA client env.`,
      error
    )
    return null
  }
}

function normalizeLanguageKey(language: string | undefined): string {
  return language?.trim().toLowerCase() ?? ''
}

export function resolveAidaClientConfig(language: string | undefined): AidaClientConfig {
  return resolveAidaClientConfigFromEnv(language, import.meta.env)
}

export function resolveAidaClientConfigFromEnv(
  language: string | undefined,
  env: ImportMetaEnv
): AidaClientConfig {
  const configMap = parseAidaClientConfigMap(env.VITE_AIDA_LIBRARY_CLIENTS)
  const languageKey = normalizeLanguageKey(language)

  if (configMap) {
    const languageConfig = configMap[languageKey]

    if (isCompleteClientConfig(languageConfig)) {
      return {
        name: languageConfig?.name,
        key: languageConfig?.key,
      }
    }

    if (languageConfig) {
      console.warn(
        `AIDA client config for language "${languageKey}" is incomplete. Falling back to default client.`
      )
    }

    const defaultConfig = configMap.default

    if (isCompleteClientConfig(defaultConfig)) {
      return {
        name: defaultConfig?.name,
        key: defaultConfig?.key,
      }
    }

    if (defaultConfig) {
      console.warn('Default AIDA client config is incomplete. Falling back to legacy env values.')
    }
  }

  const legacyConfig = legacyAidaClientConfigFromEnv(env)

  if (!isCompleteClientConfig(legacyConfig)) {
    console.warn(
      'No complete AIDA client config found. Check VITE_AIDA_LIBRARY_CLIENTS or legacy AIDA client env values.'
    )
  }

  return legacyConfig
}
